<template>
	<div class="rentabilidad_tab report_theme_container" data-theme="rentabilidad">
		<!-- Mismo set de sub_nav_filters (Mensual/Trimestral/YTD/Anual) que
			ResultadosTab. Comparten el `filterPeriod` del store financialReports
			→ cambiar el bucket acá lo refleja también en Resultados al volver. -->
		<TabHeader
			title="Rentabilidad"
			description="Analiza la tendencia y composición de la rentabilidad de tu negocio"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth" />

		<!-- ================================================================ -->
		<!-- Resumen de Ventas & Márgenes                                      -->
		<!-- 3 cards mostrando el MISMO total (ventas) bajo 3 ventanas          -->
		<!-- temporales (mes / YTD / LTM). El breakdown descompone el total     -->
		<!-- en los 5 escalones de margen — cada item tiene amount + percent.   -->
		<!-- Se pliega vía CollapsibleSection; storageKey global sincroniza el -->
		<!-- estado entre todos los tabs de finanzas.                          -->
		<!-- ================================================================ -->
		<CollapsibleSection class="mt-1"
			title="Resumen"
			storageKey="financialReports:summary-expanded">
			<template v-if="summaryHint" #hint>{{ summaryHint }}</template>
			<div class="cards_container is-flex">
				<CardVentas v-for="(card, index) in profitabilityCards" :key="index" v-bind="card" class="mr-4" />
			</div>
		</CollapsibleSection>

		<!-- ================================================================ -->
		<!-- Evolución de Rentabilidad                                         -->
		<!-- Reusa FinancialResultsTable (misma usada por ResultadosTab) con    -->
		<!-- las filas filtradas y reordenadas para mostrar Ventas + 5          -->
		<!-- escalones de margen, cada uno con su pareja money/%.               -->
		<!-- ================================================================ -->
		<FinancialResultsTable
			class="mt-2"
			:rows="profitabilityRows"
			:headers="getHeadersTable"
			:isLoading="isLoading"
			:selectedPeriodRange="selectedPeriodRange"
			:periodOptions="periodOptions"
			:filterPeriod="filterPeriod"
			@update-period="updateSelectedPeriodRange">
			<template #title>
				<ChartLine class="title_icon mr-2" />
				<span class="tittle_section">Evolución de Rentabilidad</span>
			</template>
			<template #header-actions>
				<!-- Controles placeholders. Comportamiento (gráfico comparado,
					vista de barras) queda pendiente de definición. -->
				<button class="action_btn mr-2" disabled>
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Comparación
				</button>
				<button class="action_btn mr-2" disabled>
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Ver Barra
				</button>
			</template>
		</FinancialResultsTable>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import ChartLine from "@/components/svg/ChartLine.vue";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import CardVentas from "@/components/financialReports/CardVentas.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";
import {
	PERIOD_TYPES,
	FILTER_PERIOD_OPTIONS,
	PROFITABILITY_CARD_PROGRESS_COLORS,
} from "@/config/reportStructure";
import { formatPercent, formatRangeLabel } from "@/views/financialReports/utils/periodFormatting";
import { emptyCard } from "@/views/financialReports/utils/summaryCards";
import { parseToMoney } from "@/utils/currency";
import { notificate } from "@/utils/message";

// Las filas de Rentabilidad CONSUMEN las filas ya calculadas del store
// (tableRowHeaders): desde el plan Productos (D2/D3, 2026-08-20) las fórmulas
// de los escalones viven en config/reportStructure.js — una sola aritmética
// para ambos super_types (las claves del otro tipo vienen vacías y restan 0)
// — y acá solo se elige QUÉ filas mostrar, con el theme propio del tab.
// `productsOnly: true` = filas que solo aplican a Productos (Servicios se
// reduce a Ventas → Bruto → EBITDA → Neto, mismo criterio que
// ProfitabilitySummaryCardsUseCase en el backend para las cards).
const PROFITABILITY_ROWS = [
	{ key: "ventas_netas" },
	{ key: "margen_producto_amount", productsOnly: true },
	{ key: "margen_producto", productsOnly: true, theme: "theme_row_purple" },
	{ key: "margen_bruto_amount" },
	{ key: "margen_bruto", theme: "theme_row_purple" },
	{ key: "margen_contribucion_amount", productsOnly: true },
	{ key: "margen_contribucion", productsOnly: true, theme: "theme_row_azure" },
	{ key: "EBITDA" },
	{ key: "margen_EBITDA_%", theme: "theme_row_purple" },
	{ key: "utilidad_antes_de_impuestos" },
	{ key: "margen_neto_%", theme: "theme_row_yellow" },
];

// Placeholder cuando el endpoint aún no respondió (mounted inicial). Devuelve
// una card "vacía" pero estructuralmente válida para que CardVentas renderice
// sin v-if/v-else fragmentado en el template. Misma técnica que NoOperTab.
const EMPTY_CARD = () => emptyCard();

export default {
	name: "RentabilidadTab",

	components: {
		TabHeader,
		CollapsibleSection,
		ChartLine,
		CardVentas,
		FinancialResultsTable,
	},

	data() {
		return {
			periodOptions: PERIOD_TYPES,
			filterOptions: FILTER_PERIOD_OPTIONS,
		};
	},

	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange"]),
		...mapState("financialReports", [
			"filterPeriod",
			"tableRowHeaders",
			"isLoading",
		]),
		...mapState("profitabilityReport", ["summaryCards", "summaryError"]),
		...mapGetters("financialReports", {
			getHeadersTableFunc: "getHeadersTable",
		}),

		getNow() {
			return moment().toDate();
		},

		// Hint compacto al lado del label "Resumen" cuando la sección está
		// plegada — preserva el monto del mes actual (ventas netas) para no
		// perder contexto crítico al ocultar las cards.
		summaryHint() {
			const amount = this.summaryCards && this.summaryCards.current_month
				? this.summaryCards.current_month.amount
				: null;
			return amount != null ? parseToMoney(amount) : "";
		},

		getHeadersTable() {
			return this.getHeadersTableFunc();
		},

		// Discriminador del tipo de negocio derivado del `all_keys` que backend
		// devuelve en getResultStateByPeriod: solo Productos incluye
		// `margen_producto` en all_keys, lo que marca isShowed=true en la fila
		// del store. Antes del primer fetch isShowed es undefined → tratamos
		// como Servicios (caso degradado más conservador: oculta filas en vez
		// de mostrar valores duplicados).
		isProductsBusiness() {
			return this.tableRowHeaders?.margen_producto?.isShowed === true;
		},

		profitabilityRows() {
			const isProducts = this.isProductsBusiness;
			return PROFITABILITY_ROWS
				.filter(spec => isProducts || !spec.productsOnly)
				.map(spec => {
					const row = this.tableRowHeaders[spec.key] || {};
					return {
						id: spec.key,
						label: row.label,
						isMoney: row.isMoney,
						theme: spec.theme,
						itemsDict: row.itemsDict || {},
					};
				});
		},

		profitabilityCards() {
			if (!this.summaryCards) {
				return [EMPTY_CARD(), EMPTY_CARD(), EMPTY_CARD()];
			}
			const { current_month, ytd, ltm } = this.summaryCards;
			return [
				this.buildCard(current_month, { showMom: true }),
				this.buildCard(ytd),
				this.buildCard(ltm),
			];
		},

		fetchSignature() {
			return [this.filterPeriod, this.endMonth, this.selectedPeriodRange].join("|");
		},

		// Las cards sólo dependen del mes (rangos current_month/YTD/LTM se
		// calculan en backend). Aislar la firma evita refetches cuando cambia
		// granularidad o rango de la tabla.
		summarySignature() {
			return moment(this.endMonth).format("YYYY-MM");
		},
	},

	mounted() {
		this.fetchReportData();
		this.fetchProfitabilitySummary();
	},

	methods: {
		...mapActions("financialReports", ["fetchReportData"]),
		...mapActions("profitabilityReport", ["fetchProfitabilitySummary"]),
		...mapActions("reportsFilters", ["setPeriodRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateSelectedPeriodRange(range) {
			this.setPeriodRange(range);
		},

		// Mapeo card del backend → shape de CardVentas. Mismo patrón que
		// NoOperTab.buildCard. El index del breakdown asigna color por slot
		// (--progress-bar-1..5) — backend garantiza orden Producto→Bruto→...→Neto.
		buildCard(card, { showMom = false } = {}) {
			return {
				period: formatRangeLabel(card.period_start, card.period_end),
				amount: card.amount,
				mom: showMom ? formatPercent(card.mom) : "",
				yoy: formatPercent(card.yoy),
				progressItems: (card.breakdown || []).map((item, i) => ({
					label: item.label,
					percent: item.percent,
					amount: item.amount,
					color: PROFITABILITY_CARD_PROGRESS_COLORS[i % PROFITABILITY_CARD_PROGRESS_COLORS.length],
				})),
			};
		},

	},

	watch: {
		fetchSignature() {
			this.fetchReportData();
		},
		summarySignature() {
			this.fetchProfitabilitySummary();
		},
		// El fetch del resumen falla en silencio (las cards quedan vacías). Un
		// toast hace visible el error sin romper el layout de las cards.
		summaryError(message) {
			if (message) notificate(message, "is-danger is-light", "is-top-right");
		},
	},
};
</script>

<style scoped lang="scss">
.rentabilidad_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// Header común vive en TabHeader.vue.

.title_icon {
	width: 18px;
	height: 18px;
	color: var(--report-text-muted);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text);
}

.cards_container {
	width: 100%;
	flex-wrap: wrap;
}

.action_btn {
	background: white;
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover:not(:disabled) { background: var(--report-surface); }

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
}
</style>
